import React from 'react';
import { Info, Car, Route, Shield, Gauge, CloudRain, MapPin, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о путешествиях на автомобиле и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Путешествия на автомобиле по России: полезная информация
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Route className="w-5 h-5 text-blue-500" />
          Особенности автомобильных дорог в России
        </h3>
        <p>
          Россия обладает обширной дорожной сетью, включающей в себя более 1,5 млн км дорог различного качества и назначения. 
          Основную транспортную структуру составляют <strong>федеральные трассы</strong>, соединяющие крупнейшие 
          города и регионы страны. Наиболее важными являются трассы М1 «Беларусь» (Москва — граница с Беларусью), 
          М4 «Дон» (Москва — Новороссийск), М5 «Урал» (Москва — Челябинск), М7 «Волга» (Москва — Уфа) и другие.
        </p>

        <h3 className="flex items-center gap-2">
          <Car className="w-5 h-5 text-blue-500" />
          Классификация дорог
        </h3>
        <ul>
          <li><strong>Автомагистрали (скоростные дороги)</strong> — обозначаются буквой М, имеют разделенные проезжие части, 
            ограничение скорости до 110 км/ч. Примеры: участки трасс M4 «Дон», M11 «Нева».</li>
          <li><strong>Федеральные трассы</strong> — основные дороги государственного значения, связывающие Москву с 
            административными центрами и границами государства. Обозначаются буквами М, Р, А с последующими цифрами.</li>
          <li><strong>Региональные дороги</strong> — соединяют административные центры субъектов РФ между собой и с федеральными 
            трассами. Качество покрытия может варьироваться.</li>
          <li><strong>Местные дороги</strong> — соединяют небольшие населенные пункты, качество может быть низким, 
            особенно в отдаленных регионах.</li>
          <li><strong>Платные участки</strong> — современные дороги с высоким качеством покрытия, расположены 
            преимущественно на федеральных трассах. Оплата производится на пунктах взимания платы или через транспондер.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-blue-500" />
          Ограничения скорости в России
        </h3>
        <ul>
          <li><strong>В населенных пунктах</strong>: 60 км/ч</li>
          <li><strong>Вне населенных пунктов</strong>: 90 км/ч</li>
          <li><strong>На автомагистралях</strong>: 110 км/ч</li>
          <li><strong>На скоростных дорогах</strong>: 110 км/ч (может быть увеличено до 130 км/ч на отдельных участках)</li>
        </ul>
        <p>
          Обратите внимание, что превышение скорости более чем на 20 км/ч влечет за собой штраф. 
          Штрафы увеличиваются в зависимости от величины превышения скорости.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Платные дороги в России</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
            Основные платные участки: части трасс M4 «Дон», M11 «Нева», M3 «Украина», участок ЦКАД вокруг Москвы.
            Стоимость проезда зависит от категории транспортного средства, времени суток и способа оплаты.
          </p>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Рекомендуется использовать транспондер T-pass для быстрого проезда и получения скидок на платных участках.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <CloudRain className="w-5 h-5 text-blue-500" />
          Сезонные особенности
        </h3>
        <p>
          Климатические условия существенно влияют на передвижение по дорогам России:
        </p>
        <ul>
          <li><strong>Лето (июнь-август)</strong>: оптимальный сезон для путешествий, большинство дорог в хорошем 
            состоянии, возможны пробки на курортных направлениях (особенно в сторону Черного моря).</li>
          <li><strong>Осень (сентябрь-ноябрь)</strong>: дожди могут ухудшать видимость и сцепление с дорогой, 
            в северных регионах возможны ранние заморозки.</li>
          <li><strong>Зима (декабрь-февраль)</strong>: сложный период для вождения, особенно в северных и восточных 
            регионах. Обязательно использование зимней резины с 1 декабря по 28 февраля. В Сибири и на Дальнем Востоке 
            морозы могут достигать -40°C и ниже.</li>
          <li><strong>Весна (март-май)</strong>: таяние снега может приводить к подтоплениям дорог, возможны паводки на малых реках.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-500" />
          Популярные маршруты для автопутешествий
        </h3>
        <ul>
          <li><strong>Москва — Санкт-Петербург</strong>: Около 700 км, оптимальное время в пути 8-9 часов по трассе М11 «Нева» (платная) 
            или 10-11 часов по М10 «Россия» (бесплатная альтернатива).</li>
          <li><strong>Золотое кольцо России</strong>: Кольцевой маршрут по старинным городам северо-восточной части России — 
            Сергиев Посад, Переславль-Залесский, Ростов Великий, Ярославль, Кострома, Иваново, Суздаль, Владимир. 
            Общая протяженность около 700 км.</li>
          <li><strong>Москва — Казань</strong>: Около 815 км по трассе М7 «Волга», время в пути около 10-12 часов.</li>
          <li><strong>Краснодар — Сочи</strong>: Около 280 км по трассе А147, время в пути 4-5 часов.</li>
          <li><strong>Владивосток — Находка</strong>: Около 170 км, живописный маршрут по побережью Японского моря.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Особо сложные участки дорог
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li><strong>Трасса «Лена» (Якутск — Магадан)</strong>: Отдельные участки грунтовой дороги с крайне сложным покрытием</li>
            <li><strong>Трасса «Колыма» (Якутск — Магадан)</strong>: Экстремальный маршрут, требующий специальной подготовки</li>
            <li><strong>Горные дороги Кавказа и Алтая</strong>: Крутые подъемы, узкие участки, возможны камнепады</li>
            <li><strong>Перевалы в Сибири</strong>: Сложные погодные условия, резкие перепады высот</li>
            <li><strong>Зимники</strong>: Временные дороги на замерзших водоемах, доступны только зимой в северных регионах</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Безопасность на дорогах России
        </h3>
        <p>
          Для обеспечения безопасности в дальних поездках по России рекомендуется:
        </p>
        <ol>
          <li><strong>Планировать поездку заранее</strong>, изучать маршрут и специфику трасс</li>
          <li><strong>Проверять техническое состояние автомобиля</strong> перед отправлением</li>
          <li><strong>Соблюдать скоростной режим</strong> и правила дорожного движения</li>
          <li><strong>Делать регулярные остановки</strong> для отдыха (каждые 2-3 часа)</li>
          <li><strong>Иметь при себе необходимые документы</strong>: водительское удостоверение, СТС, ОСАГО</li>
          <li><strong>Заправляться заблаговременно</strong>, особенно на дальних маршрутах, где АЗС могут быть редкими</li>
          <li><strong>Всегда иметь запас воды и непортящихся продуктов</strong> на случай непредвиденных задержек</li>
          <li><strong>Использовать навигаторы с оффлайн-картами</strong>, поскольку мобильная связь доступна не везде</li>
        </ol>

        <h3>Нормы расхода топлива по категориям транспорта</h3>
        <p>
          Для планирования бюджета поездки полезно знать ориентировочные нормы расхода топлива:
        </p>
        <ul>
          <li><strong>Легковые автомобили</strong>: от 5 до 15 л на 100 км (зависит от объема двигателя, типа топлива и стиля вождения)</li>
          <li><strong>Внедорожники и кроссоверы</strong>: от 8 до 20 л на 100 км</li>
          <li><strong>Микроавтобусы</strong>: от 10 до 15 л на 100 км</li>
          <li><strong>Грузовые автомобили малой грузоподъемности</strong>: от 15 до 25 л на 100 км</li>
        </ul>
        <p>
          Расход топлива увеличивается при движении в городском цикле, в горной местности, с полной загрузкой автомобиля 
          и при использовании кондиционера.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор времени в пути поможет вам спланировать поездку с учетом особенностей дорог, 
          погодных условий и необходимых остановок для комфортного и безопасного путешествия по России!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;